import { useTranslation } from "react-i18next";
import { User, Calendar, Flag, Global, Heart, BookSaved, Location } from "iconsax-react";
import { FileViewer } from "@/shared/ui";
import { formatDateForDisplay, getFamilyStatusLabel } from "@/shared/utils";
import { getNationalityLabel } from "@/features/hr/hiring";
import type { GeneralSection as GeneralSectionType } from "../../types/personalSheet";
import ProfileDataRow from "../ProfileDataRow";

interface Props {
  general: GeneralSectionType;
  enrollmentVerificationUrl?: string | null;
  workerAgreementUrl?: string | null;
}

export default function GeneralSection({ general, enrollmentVerificationUrl, workerAgreementUrl }: Props) {
  const { t, i18n } = useTranslation("EmployeePortal");
  const { t: tCommon } = useTranslation("Common");
  const { t: tApply } = useTranslation("ApplyApplicationPage");
  const locale = i18n.language as "ru" | "kk" | "en";

  const cityName = general.city_of_birth
    ? locale === "kk"
      ? general.city_of_birth.name_kk
      : locale === "en"
        ? general.city_of_birth.name_en
        : general.city_of_birth.name_ru
    : general.city_of_birth_other;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col">
        <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.dateOfBirth")} value={formatDateForDisplay(general.date_of_birth)} />
        <ProfileDataRow icon={User} label={t("personalSheet.fields.gender")} value={general.gender ? t(`personalSheet.genderValues.${general.gender}`) : null} />
        <ProfileDataRow icon={Heart} label={t("personalSheet.fields.familyStatus")} value={general.family_status ? getFamilyStatusLabel(general.family_status, general.gender, tCommon) : null} />
        <ProfileDataRow icon={Flag} label={t("personalSheet.fields.nationality")} value={getNationalityLabel(general.nationality, general.gender, tApply)} />
        <ProfileDataRow icon={Global} label={t("personalSheet.fields.citizenship")} value={general.citizenship} />
        <ProfileDataRow icon={BookSaved} label={t("personalSheet.fields.isResident")} value={t(`personalSheet.booleanValues.${general.is_resident ? "yes" : "no"}`)} />
        <ProfileDataRow icon={BookSaved} label={t("personalSheet.fields.isStudent")} value={t(`personalSheet.booleanValues.${general.is_student ? "yes" : "no"}`)} />
        {general.is_student && general.enrollment_start_date && (
          <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.enrollmentStartDate")} value={formatDateForDisplay(general.enrollment_start_date)} />
        )}
        {general.is_student && general.enrollment_end_date && (
          <ProfileDataRow icon={Calendar} label={t("personalSheet.fields.enrollmentEndDate")} value={formatDateForDisplay(general.enrollment_end_date)} />
        )}
        <ProfileDataRow icon={Location} label={t("personalSheet.fields.cityOfBirth")} value={cityName} />
      </div>
      {general.is_student && enrollmentVerificationUrl && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.enrollmentVerificationFile")}</span>
          <FileViewer existingFileUrl={enrollmentVerificationUrl} isDownloadable />
        </div>
      )}
      {workerAgreementUrl && (
        <div className="flex flex-col gap-1.5">
          <span className="text-label-sm content-base-secondary">{t("personalSheet.fields.workerAgreementFile")}</span>
          <FileViewer existingFileUrl={workerAgreementUrl} isDownloadable />
        </div>
      )}
    </div>
  );
}
